import axios from "axios";
import { IDataContext } from "../interfaces/IDataContext";
import { handleMessage } from "../helpers/stateHelper";
import { IUser } from "../interfaces/IUser";
import { ICustomer } from "../interfaces/ICustomer";
import { ICreateTicketRequest } from "../interfaces/ICreateTicketRequest";
import { ITicket } from "../interfaces/ITicket";
import { generateZip } from "../helpers/fileHelper";

export default class CustomerOrderApi {
  public getOrders = async (
    dataContext: IDataContext,
    user: IUser,
    customer: ICustomer,
    t: any,
    pageToken?: string,
    searchTerm?: string
  ): Promise<void> => {
    const isLoadingMore = !!pageToken;

    dataContext.setRootState((prev) => ({
      ...prev,
      isLoading: !isLoadingMore,
      isFetchingMore: isLoadingMore,
    }));

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/get`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            customerName: customer.title,
            pageSize: 20,
            ...(pageToken && { pageToken }),
            ...(searchTerm && { searchTerm }),
          },
        }
      );

      const { orders, nextPageToken } = response.data;

      dataContext.setRootState((prev) => ({
        ...prev,
        orders: isLoadingMore ? [...prev.orders, ...(orders ?? [])] : (orders ?? []),
        nextPageToken: nextPageToken ?? null,
      }));
    } catch (error: any) {
      handleMessage(dataContext.setRootState, "error", t("error.orders"));
    } finally {
      dataContext.setRootState((prev) => ({
        ...prev,
        isLoading: false,
        isFetchingMore: false,
      }));
    }
  };

  public createTicket = async (
    dataContext: IDataContext,
    user: IUser,
    customer: ICustomer,
    ticket: ICreateTicketRequest,
    image: File | null,
    t: any
  ): Promise<{ ticketNumber: string; customerFound: boolean } | null> => {
    try {
      const formData = new FormData();
      formData.append('customerName', customer.title);
      formData.append('reportedByName', user.title ?? user.username);
      formData.append('title', ticket.title);
      formData.append('orderNumber', ticket.orderNumber);
      formData.append('phoneNumber', ticket.phoneNumber);
      formData.append('email', ticket.email);
      formData.append('category', ticket.category);
      formData.append('componentName', ticket.componentName);
      formData.append('faultSymptom', ticket.faultSymptom);
      formData.append('replacementMaterial', String(ticket.replacementMaterial));
      if (image) formData.append('image', image, image.name);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/tickets/create`,
        formData,
        {
          headers: { Authorization: `Bearer ${user.token}` },
        }
      );
      const { ticketNumber, customerFound } = response.data;
      handleMessage(dataContext.setRootState, "success", t("success.ticketCreated", { number: ticketNumber }));
      if (!customerFound) {
        handleMessage(dataContext.setRootState, "warning", t("warning.customerNotFound"));
      }
      return { ticketNumber, customerFound };
    } catch (error: any) {
      handleMessage(dataContext.setRootState, "error", t("error.ticketCreate"));
      return null;
    }
  };

  public getTickets = async (
    dataContext: IDataContext,
    user: IUser,
    customer: ICustomer,
    t: any,
    pageToken?: string
  ): Promise<void> => {
    const isLoadingMore = !!pageToken;

    dataContext.setRootState((prev) => ({
      ...prev,
      isLoadingTickets: !isLoadingMore,
      isLoadingMoreTickets: isLoadingMore,
    }));

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/tickets/get`,
        {
          headers: { Authorization: `Bearer ${user.token}` },
          params: {
            customerName: customer.title,
            pageSize: 20,
            ...(pageToken && { pageToken }),
          },
        }
      );
      const tickets = response.data ?? [];
      dataContext.setRootState((prev) => ({
        ...prev,
        tickets: isLoadingMore ? [...prev.tickets, ...tickets] : tickets,
        ticketsNextPageToken: null,
      }));
    } catch {
      handleMessage(dataContext.setRootState, "error", t("error.tickets"));
    } finally {
      dataContext.setRootState((prev) => ({
        ...prev,
        isLoadingTickets: false,
        isLoadingMoreTickets: false,
      }));
    }
  };

  public downloadFolder = async (
    dataContext: IDataContext,
    user: IUser,
    folderId: string,
    orderNumber: string,
    t: any
  ): Promise<void> => {
    dataContext.setRootState((prev) => ({
      ...prev,
      folderIsDownLoading: folderId,
    }));
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orders/order/zip/get`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          params: {
            folderId,
            orderNumber,
            responseType: "blob",
          },
        }
      );

      if (response?.data) {
        const { fileContent, fileName } = response.data;
        generateZip(fileContent, fileName);
      }
    } catch (error: any) {
      handleMessage(dataContext.setRootState, "error", t("error.zip"));
    } finally {
      dataContext.setRootState((prev) => ({
        ...prev,
        folderIsDownLoading: "",
      }));
      handleMessage(
        dataContext.setRootState,
        "success",
        `${t("success.downloaded")}`
      );
    }
  };
}
